import axios from 'axios';

const API_BASE_URL = 'https://keyorbit.onrender.com';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('keyorbit_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('keyorbit_user');
      localStorage.removeItem('keyorbit_token');
      localStorage.removeItem('pendingRegistrationId');
      
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login?session=expired';
      }
    }
    
    if (error.response?.status === 403) {
      if (!window.location.pathname.includes('/unauthorized')) {
        window.location.href = '/unauthorized';
      }
    }
    
    return Promise.reject(error);
  }
);

// Helper function to get auth token
const getToken = () => localStorage.getItem('keyorbit_token');

// Auth API calls
export const authAPI = {
  // Step 1 & 2: Register user with all data (user + organization)
  register: (userData) => api.post('/auth/register', userData),
  
  // Step 3: Verify email and create user/organization
  verifyEmail: (code) => api.post('/auth/verify-email', { code }),
  
  // Resend verification code (accepts { email } or { pendingId })
  resendVerification: (data) => api.post('/auth/resend-verification', data),
  
  // Login with email/password
  login: (email, password) => api.post('/auth/login', { email, password }),
  
  // Google OAuth for LOGIN ONLY
  googleLogin: () => window.location.href = `${API_BASE_URL}/auth/google`,
  
  // Get Google OAuth URL (for frontend redirect)
  // getGoogleAuthUrl: () => `${API_BASE_URL}/auth/google`,
  
  // Google OAuth registration (disabled but kept for compatibility)
  // googleRegister: (data) => api.post('/auth/google/register', data),
  
  // Check if email exists
  checkEmail: (email) => api.post('/auth/check-email', { email }),
  
  // Password reset
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, newPassword) => api.post('/auth/reset-password', { token, newPassword }),
  validateResetToken: (token) => api.get(`/auth/validate-reset-token/${token}`),
  
  // Profile
  getProfile: () => api.get('/auth/profile'),
  logout: () => api.post('/auth/logout'),
  
  // Get pending registration status
  getPendingRegistration: (pendingId) => api.get(`/auth/registration/${pendingId}`),
  
  // Update pending registration with organization data
  updateRegistrationWithOrganization: (pendingId, organizationData) => 
    api.post(`/auth/registration/${pendingId}/organization`, organizationData),
};

// Registration helper functions
export const registrationHelper = {
  // Complete full registration flow (user + organization)
  completeRegistration: async (userData, organizationData) => {
    try {
      // Combine all data
      const completeData = {
        firstName: userData.firstName,
        lastName: userData.lastName,
        email: userData.email,
        phone: userData.phone,
        password: userData.password,
        organizationData: organizationData
      };
      
      console.log('Sending registration data:', { 
        email: completeData.email,
        hasOrganizationData: !!organizationData 
      });
      
      // Send registration request
      const response = await authAPI.register(completeData);
      
      if (response.data.pendingId) {
        // Store pending ID for verification step
        localStorage.setItem('pendingRegistrationId', response.data.pendingId);
        
        console.log('Registration successful, pendingId:', response.data.pendingId);
        
        return {
          success: true,
          pendingId: response.data.pendingId,
          email: userData.email,
          message: response.data.message || 'Verification email sent successfully'
        };
      }
      
      throw new Error('Registration failed: No pending ID received');
      
    } catch (error) {
      console.error('Registration error:', error.response?.data || error.message);
      
      let errorMessage = 'Registration failed. Please try again.';
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      throw new Error(errorMessage);
    }
  },
  
  // Verify email and complete registration
  verifyAndComplete: async (code) => {
    try {
      console.log('Verifying code:', code);
      
      const response = await authAPI.verifyEmail(code);
      
      if (response.data.user && response.data.token) {
        // Store session
        localStorage.setItem('keyorbit_user', JSON.stringify(response.data.user));
        localStorage.setItem('keyorbit_token', response.data.token);
        
        // Clear pending registration data
        localStorage.removeItem('pendingRegistrationId');
        
        console.log('Verification successful, user created:', response.data.user.email);
        
        return {
          success: true,
          user: response.data.user,
          token: response.data.token,
          message: 'Account created successfully'
        };
      }
      
      throw new Error('Verification failed: No user data received');
      
    } catch (error) {
      console.error('Verification error:', error.response?.data || error.message);
      
      let errorMessage = 'Verification failed. Please try again.';
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      throw new Error(errorMessage);
    }
  },
  
  // Resend verification code
  resendVerificationCode: async (emailOrPendingId) => {
    try {
      console.log('Resending verification for:', emailOrPendingId);
      
      // Determine if it's an email or pending ID
      const data = emailOrPendingId.includes('@') 
        ? { email: emailOrPendingId }
        : { pendingId: emailOrPendingId };
      
      const response = await authAPI.resendVerification(data);
      
      if (response.data.success) {
        console.log('Resend successful, pendingId:', response.data.pendingId);
        
        return {
          success: true,
          message: response.data.message || 'Verification code resent successfully',
          pendingId: response.data.pendingId
        };
      } else {
        throw new Error(response.data.error || 'Failed to resend verification');
      }
      
    } catch (error) {
      console.error('Resend verification error:', error.response?.data || error.message);
      
      let errorMessage = 'Failed to resend verification code.';
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      throw new Error(errorMessage);
    }
  },
  
  // Check if there's a pending registration
  hasPendingRegistration: () => {
    const pendingId = localStorage.getItem('pendingRegistrationId');
    return !!pendingId;
  },
  
  // Get pending registration ID
  getPendingRegistrationId: () => {
    return localStorage.getItem('pendingRegistrationId');
  },
  
  // Clear registration data
  clearRegistrationData: () => {
    localStorage.removeItem('pendingRegistrationId');
  }
};

// Google OAuth helper
export const googleAuthHelper = {
  // Start Google OAuth flow for login
  startGoogleLogin: () => {
    const redirectUri = `${window.location.origin}/auth/google-callback`;
    const state = Math.random().toString(36).substring(7);
    localStorage.setItem('google_auth_state', state);
    
    const params = new URLSearchParams({
      client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID || 'your-google-client-id',
      redirect_uri: redirectUri,
      response_type: 'code',
      scope: 'openid email profile',
      access_type: 'offline',
      prompt: 'consent',
      state: state
    });
    
    const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
    console.log('Redirecting to Google OAuth:', authUrl);
    window.location.href = authUrl;
  },
  
  // Handle Google callback
  handleGoogleCallback: async (code, state) => {
    const savedState = localStorage.getItem('google_auth_state');
    
    if (state !== savedState) {
      throw new Error('Invalid state parameter. Possible CSRF attack.');
    }
    
    localStorage.removeItem('google_auth_state');
    
    try {
      console.log('Exchanging Google OAuth code for token');
      const response = await authAPI.googleLogin(code);
      
      if (response.data.user && response.data.token) {
        // Store session
        localStorage.setItem('keyorbit_user', JSON.stringify(response.data.user));
        localStorage.setItem('keyorbit_token', response.data.token);
        
        console.log('Google login successful, user:', response.data.user.email);
        
        return {
          success: true,
          user: response.data.user,
          token: response.data.token
        };
      }
      
      throw new Error('Google login failed: No user data received');
      
    } catch (error) {
      console.error('Google login error:', error.response?.data || error.message);
      throw error;
    }
  },
  
  // Clear Google OAuth state
  clearState: () => {
    localStorage.removeItem('google_auth_state');
  }
};

// API Tokens API calls
export const tokenAPI = {
  getTokenRealtimeDetails: async (tokenId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api-tokens/${tokenId}/realtime`, {
        headers: {
          'Authorization': `Bearer ${getToken()}`
        }
      });
      return response;
    } catch (error) {
      console.error('Error fetching token realtime details:', error);
      throw error;
    }
  },
  
  getTokens: () => api.get('/api-tokens'),
  createToken: (tokenData) => api.post('/api-tokens', tokenData),
  getTokenDetails: (tokenId) => api.get(`/api-tokens/${tokenId}`),
  regenerateToken: (tokenId) => api.post(`/api-tokens/${tokenId}/regenerate`),
  revokeToken: (tokenId) => api.post(`/api-tokens/${tokenId}/revoke`),
  updateTokenPermissions: (tokenId, permissions, scopes) => 
    api.put(`/api-tokens/${tokenId}/permissions`, { permissions, scopes }),
  getTokenUsage: (tokenId) => api.get(`/api-tokens/${tokenId}/usage`),
  testToken: (tokenValue) => api.post('/api-tokens/test', { token: tokenValue }),
  
  // Get token by value
  getTokenByValue: (tokenValue) => api.post('/api-tokens/validate', { token: tokenValue }),
  
  // Get token statistics
  getTokenStats: () => api.get('/api-tokens/stats'),
};

// User API calls
export const userAPI = {
  getCurrentUser: () => api.get('/me'),
  getMyTokens: () => api.get('/me/api-tokens'),
  getAPIUserInfo: () => api.get('/api/me'), // Uses API tokens
  
  // Get user's organization
  getMyOrganization: () => api.get('/me/organization'),
  
  // Update user profile
  updateProfile: (userData) => api.put('/me/profile', userData),
  
  // Change password
  changePassword: (currentPassword, newPassword) => 
    api.post('/me/change-password', { currentPassword, newPassword }),
  
  // Enable/disable MFA
  enableMFA: () => api.post('/me/mfa/enable'),
  disableMFA: () => api.post('/me/mfa/disable'),
  verifyMFA: (code) => api.post('/me/mfa/verify', { code }),
  
  // Get user activity
  getActivity: (limit = 50) => api.get(`/me/activity?limit=${limit}`),
  
  // Update preferences
  updatePreferences: (preferences) => api.put('/me/preferences', preferences),
};

// Organization API calls
export const organizationAPI = {
  getMyOrganization: () => api.get('/organizations/me'),
  getOrganizationUsers: (organizationId) => api.get(`/organizations/${organizationId}/users`),
  updateOrganization: (organizationData) => api.put('/organizations/me', organizationData),
  getOrganizationStats: () => api.get('/organizations/me/stats'),
  inviteUser: (email, role) => api.post('/organizations/me/invite', { email, role }),
  removeUser: (userId) => api.delete(`/organizations/me/users/${userId}`),
  
  // Get organization settings
  getSettings: () => api.get('/organizations/me/settings'),
  updateSettings: (settings) => api.put('/organizations/me/settings', settings),
  
  // Get organization audit logs
  getAuditLogs: (params = {}) => {
    const queryParams = new URLSearchParams(params).toString();
    return api.get(`/organizations/me/audit-logs${queryParams ? `?${queryParams}` : ''}`);
  },
};

// Audit Logs API calls
export const auditAPI = {
  getMyAuditLogs: (params = {}) => {
    const queryParams = new URLSearchParams(params).toString();
    return api.get(`/audit-logs/me${queryParams ? `?${queryParams}` : ''}`);
  },
  getOrganizationAuditLogs: (organizationId, params = {}) => {
    const queryParams = new URLSearchParams(params).toString();
    return api.get(`/organizations/${organizationId}/audit-logs${queryParams ? `?${queryParams}` : ''}`);
  },
  getAuthAuditLogs: () => api.get('/audit-logs/auth'),
  exportAuditLogs: (format = 'json') => api.get(`/audit-logs/export?format=${format}`),
  
  // Search audit logs
  searchAuditLogs: (query, filters = {}) => {
    const params = { query, ...filters };
    const queryParams = new URLSearchParams(params).toString();
    return api.get(`/audit-logs/search${queryParams ? `?${queryParams}` : ''}`);
  },
};

// Key Management API calls
export const keyAPI = {
  getKeys: (params = {}) => {
    const queryParams = new URLSearchParams(params).toString();
    return api.get(`/keys${queryParams ? `?${queryParams}` : ''}`);
  },
  createKey: (keyData) => api.post('/keys', keyData),
  getKey: (keyId) => api.get(`/keys/${keyId}`),
  updateKey: (keyId, keyData) => api.put(`/keys/${keyId}`, keyData),
  deleteKey: (keyId) => api.delete(`/keys/${keyId}`),
  rotateKey: (keyId) => api.post(`/keys/${keyId}/rotate`),
  exportKey: (keyId, format) => api.get(`/keys/${keyId}/export?format=${format}`),
  
  // Key operations
  encrypt: (keyId, data) => api.post(`/keys/${keyId}/encrypt`, { data }),
  decrypt: (keyId, encryptedData) => api.post(`/keys/${keyId}/decrypt`, { data: encryptedData }),
  sign: (keyId, data) => api.post(`/keys/${keyId}/sign`, { data }),
  verify: (keyId, data, signature) => api.post(`/keys/${keyId}/verify`, { data, signature }),
  
  // Key usage stats
  getKeyUsage: (keyId) => api.get(`/keys/${keyId}/usage`),
  getKeyMetadata: (keyId) => api.get(`/keys/${keyId}/metadata`),
  
  // Bulk operations
  deleteKeys: (keyIds) => api.post('/keys/bulk-delete', { keyIds }),
  rotateKeys: (keyIds) => api.post('/keys/bulk-rotate', { keyIds }),
};

// Policy Management API calls
export const policyAPI = {
  getPolicies: () => api.get('/policies'),
  createPolicy: (policyData) => api.post('/policies', policyData),
  getPolicy: (policyId) => api.get(`/policies/${policyId}`),
  updatePolicy: (policyId, policyData) => api.put(`/policies/${policyId}`, policyData),
  deletePolicy: (policyId) => api.delete(`/policies/${policyId}`),
  applyPolicy: (policyId, targetType, targetId) => 
    api.post(`/policies/${policyId}/apply`, { targetType, targetId }),
  
  // Get policy templates
  getTemplates: () => api.get('/policies/templates'),
  
  // Evaluate policy
  evaluatePolicy: (policyId, data) => api.post(`/policies/${policyId}/evaluate`, data),
};

// Dashboard API calls
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
  getRecentActivity: () => api.get('/dashboard/activity'),
  getSystemStatus: () => api.get('/dashboard/system-status'),
  getSecurityAlerts: () => api.get('/dashboard/security-alerts'),
  getUsageMetrics: (period = '30d') => api.get(`/dashboard/usage-metrics?period=${period}`),
  
  // Get dashboard widgets
  getWidgets: () => api.get('/dashboard/widgets'),
  updateWidgets: (widgets) => api.put('/dashboard/widgets', { widgets }),

  getStats: () => api.get('/dashboard/stats'),
  getActivity: (days = 7) => api.get(`/dashboard/activity?days=${days}`),
  getQuickActions: () => api.get('/dashboard/quick-actions'),
  getAlerts: () => api.get('/dashboard/alerts')
};

// Protected API calls (using API tokens - for external API access)
export const protectedAPI = {
  getKeys: () => api.get('/api/v1/keys'),
  getAuditLogs: () => api.get('/api/v1/audit/logs'),
  getOrganizationInfo: () => api.get('/api/v1/organization'),
  
  // Key operations via API tokens
  encryptData: (keyId, data) => api.post(`/api/v1/keys/${keyId}/encrypt`, { data }),
  decryptData: (keyId, encryptedData) => api.post(`/api/v1/keys/${keyId}/decrypt`, { data: encryptedData }),
  signData: (keyId, data) => api.post(`/api/v1/keys/${keyId}/sign`, { data }),
  verifySignature: (keyId, data, signature) => api.post(`/api/v1/keys/${keyId}/verify`, { data, signature }),
  
  // Health check
  healthCheck: () => api.get('/api/v1/health'),
  
  // Get API version
  getVersion: () => api.get('/api/v1/version'),
};

// Utility functions
export const authUtils = {
  // Check if user is authenticated
  isAuthenticated: () => {
    const token = localStorage.getItem('keyorbit_token');
    const user = localStorage.getItem('keyorbit_user');
    
    if (!token || !user) {
      return false;
    }
    
    try {
      JSON.parse(user); // Verify user data is valid JSON
      return true;
    } catch (error) {
      console.error('Invalid user data in localStorage:', error);
      return false;
    }
  },
  
  // Get current user
  getCurrentUser: () => {
    const userStr = localStorage.getItem('keyorbit_user');
    if (!userStr) return null;
    
    try {
      return JSON.parse(userStr);
    } catch (error) {
      console.error('Error parsing user data:', error);
      return null;
    }
  },
  
  // Get auth token
  getAuthToken: () => localStorage.getItem('keyorbit_token'),
  
  // Clear auth data
  clearAuthData: () => {
    localStorage.removeItem('keyorbit_user');
    localStorage.removeItem('keyorbit_token');
    localStorage.removeItem('pendingRegistrationId');
    localStorage.removeItem('google_auth_state');
  },
  
  // Set auth data
  setAuthData: (user, token) => {
    try {
      localStorage.setItem('keyorbit_user', JSON.stringify(user));
      localStorage.setItem('keyorbit_token', token);
    } catch (error) {
      console.error('Error setting auth data:', error);
    }
  },
  
  // Check user role
  hasRole: (role) => {
    const user = authUtils.getCurrentUser();
    return user?.role === role;
  },
  
  // Check if user is admin (all UI registered users are admins)
  isAdmin: () => {
    const user = authUtils.getCurrentUser();
    return user?.role === 'admin';
  },
  
  // Check if user has organization
  hasOrganization: () => {
    const user = authUtils.getCurrentUser();
    return user?.organization?.id !== undefined;
  },
  
  // Get user's organization ID
  getOrganizationId: () => {
    const user = authUtils.getCurrentUser();
    return user?.organization?.id;
  },
  
  // Get user's organization name
  getOrganizationName: () => {
    const user = authUtils.getCurrentUser();
    return user?.organization?.name;
  },
  
  // Check if user's email is verified
  isEmailVerified: () => {
    const user = authUtils.getCurrentUser();
    return user?.isVerified === true;
  },
  
  // Get user's full name
  getFullName: () => {
    const user = authUtils.getCurrentUser();
    if (!user) return '';
    return `${user.firstName || ''} ${user.lastName || ''}`.trim();
  },
  
  // Get user's initials
  getInitials: () => {
    const user = authUtils.getCurrentUser();
    if (!user) return '';
    
    const first = user.firstName?.charAt(0) || '';
    const last = user.lastName?.charAt(0) || '';
    return (first + last).toUpperCase();
  }
};

// API call wrapper with enhanced error handling
export const apiCall = async (apiFunction, ...args) => {
  try {
    console.log(`API Call: ${apiFunction.name || 'anonymous'}`, args);
    
    const response = await apiFunction(...args);
    
    console.log(`API Response: ${response.status}`, response.data);
    
    return {
      success: true,
      data: response.data,
      status: response.status,
      headers: response.headers
    };
  } catch (error) {
    // Enhanced error handling
    if (error.response) {
      // Server responded with error
      const errorMessage = error.response.data?.error || 
                          error.response.data?.message || 
                          error.response.data?.detail ||
                          'Server error occurred';
      const status = error.response.status;
      
      console.error(`API Error ${status}:`, errorMessage, error.response.data);
      
      // Special handling for common errors
      if (status === 401) {
        // Unauthorized - clear auth data
        authUtils.clearAuthData();
        
        // Only redirect if not already on login page
        if (!window.location.pathname.includes('/login')) {
          setTimeout(() => {
            window.location.href = '/login?session=expired';
          }, 100);
        }
      } else if (status === 403) {
        // Forbidden - redirect to unauthorized page
        if (!window.location.pathname.includes('/unauthorized')) {
          setTimeout(() => {
            window.location.href = '/unauthorized';
          }, 100);
        }
      } else if (status === 400) {
        // Bad request - show validation errors
        const validationErrors = error.response.data?.errors || error.response.data?.validation;
        if (validationErrors) {
          console.error('Validation errors:', validationErrors);
        }
      }
      
      return {
        success: false,
        error: errorMessage,
        status: status,
        data: error.response.data,
        isNetworkError: false,
        validationErrors: error.response.data?.errors || error.response.data?.validation
      };
      
    } else if (error.request) {
      // No response received (network error)
      console.error('Network Error:', error.message);
      
      return {
        success: false,
        error: 'Network error. Please check your connection and try again.',
        status: 0,
        data: null,
        isNetworkError: true
      };
      
    } else {
      // Request setup error
      console.error('Request Setup Error:', error.message);
      
      return {
        success: false,
        error: `Request failed: ${error.message}`,
        status: -1,
        data: null,
        isNetworkError: false
      };
    }
  }
};

// Registration flow utility
export const registrationFlow = {
  // Step 1: Collect user data
  collectUserData: () => {
    const userData = {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      password: ''
    };
    
    return userData;
  },
  
  // Step 2: Collect organization data
  collectOrganizationData: () => {
    const organizationData = {
      organizationName: '',
      domain: '',
      industry: '',
      companySize: '',
      role: ''
    };
    
    return organizationData;
  },
  
  // Validate user data
  validateUserData: (userData) => {
    const errors = {};
    
    if (!userData.firstName?.trim()) errors.firstName = 'First name is required';
    if (!userData.lastName?.trim()) errors.lastName = 'Last name is required';
    if (!userData.email) errors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(userData.email)) errors.email = 'Invalid email format';
    if (!userData.phone) errors.phone = 'Phone number is required';
    if (!userData.password) errors.password = 'Password is required';
    else if (userData.password.length < 8) errors.password = 'Password must be at least 8 characters';
    
    return errors;
  },
  
  // Validate organization data
  validateOrganizationData: (organizationData) => {
    const errors = {};
    
    if (!organizationData.organizationName?.trim()) errors.organizationName = 'Organization name is required';
    if (!organizationData.domain?.trim()) errors.domain = 'Domain is required';
    if (!organizationData.industry) errors.industry = 'Industry is required';
    if (!organizationData.companySize) errors.companySize = 'Company size is required';
    if (!organizationData.role) errors.role = 'Role is required';
    
    return errors;
  },
  
  // Get registration progress
  getRegistrationProgress: () => {
    const pendingId = localStorage.getItem('pendingRegistrationId');
    return {
      hasPendingRegistration: !!pendingId,
      pendingId: pendingId
    };
  },
  
  // Clear registration progress
  clearRegistrationProgress: () => {
    localStorage.removeItem('pendingRegistrationId');
  },
  
  // Get registration steps
  getSteps: () => [
    { number: 1, title: 'Account Details', description: 'Create your user account' },
    { number: 2, title: 'Organization', description: 'Set up your organization' },
    { number: 3, title: 'Verification', description: 'Verify your email' }
  ]
};

// Health check utility
export const healthCheck = {
  // Check if backend is reachable
  checkBackend: async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/health`, { timeout: 5000 });
      return {
        online: true,
        status: response.status,
        data: response.data
      };
    } catch (error) {
      return {
        online: false,
        error: error.message,
        status: error.response?.status
      };
    }
  },
  
  // Check authentication status
  checkAuth: async () => {
    try {
      const response = await api.get('/auth/health');
      return {
        authenticated: true,
        status: response.status,
        data: response.data
      };
    } catch (error) {
      return {
        authenticated: false,
        error: error.message,
        status: error.response?.status
      };
    }
  }
};

// Export default axios instance
export default api;

// // Export all API modules for convenience
// export const API = {
//   auth: authAPI,
//   registration: registrationHelper,
//   google: googleAuthHelper,
//   tokens: tokenAPI,
//   user: userAPI,
//   organization: organizationAPI,
//   audit: auditAPI,
//   keys: keyAPI,
//   policies: policyAPI,
//   dashboard: dashboardAPI,
//   protected: protectedAPI,
//   utils: authUtils,
//   flow: registrationFlow,
//   health: healthCheck,
//   call: apiCall
// };

// User Management API calls
export const userManagementAPI = {
  // Get all users in organization
  getUsers: () => api.get('/users'),
  
  // Get user statistics
  getUserStats: () => api.get('/users/stats'),
  
  // Invite user to organization
  inviteUser: (userData) => {
    // Normalize role to lowercase for backend
    const normalizedData = {
      ...userData,
      role: (userData.role || 'user').toLowerCase()
    };
    return api.post('/users/invite', normalizedData);
  },
  
  // Get user details
  getUserDetails: (userId) => api.get(`/users/${userId}`),
  
  // Update user information
  updateUser: (userId, userData) => {
    // Normalize role if provided
    if (userData.role) {
      userData.role = userData.role.toLowerCase();
    }
    return api.put(`/users/${userId}`, userData);
  },
  
  // Update user status (activate, deactivate, suspend)
  updateUserStatus: (userId, status) => api.post(`/users/${userId}/status`, { status }),
  
  // Update user role
  updateUserRole: (userId, role) => {
    const normalizedRole = role.toLowerCase();
    return api.post(`/users/${userId}/role`, { role: normalizedRole });
  },
  
  // Delete user (soft delete)
  deleteUser: (userId) => api.delete(`/users/${userId}`),
  
  // Bulk user actions
  bulkUserActions: (actionData) => {
    // Normalize role if provided in bulk action
    if (actionData.role) {
      actionData.role = actionData.role.toLowerCase();
    }
    return api.post('/users/bulk/actions', actionData);
  },
  
  // Get pending invitations
  getInvitations: () => api.get('/invitations'),
  
  // Resend invitation
  resendInvitation: (invitationId) => api.post(`/invitations/${invitationId}/resend`),
  
  // Cancel invitation
  cancelInvitation: (invitationId) => api.post(`/invitations/${invitationId}/cancel`),
  
  // Admin reset password for user
  adminResetPassword: (userId) => api.post(`/users/${userId}/reset-password`),
  
  // Validate invitation token
  validateInvitation: (token) => api.get(`/auth/validate-invitation/${token}`),
  
  // Accept invitation and create account
  acceptInvitation: (acceptData) => api.post('/auth/accept-invitation', acceptData),
  
  // Get user activity logs
  getUserActivity: (userId, limit = 10) => api.get(`/users/${userId}/activity?limit=${limit}`),
  
  // Search users
  searchUsers: (query, filters = {}) => {
    const params = new URLSearchParams({ query, ...filters }).toString();
    return api.get(`/users/search?${params}`);
  },
  
  // Export users (CSV/Excel)
  exportUsers: (format = 'csv', filters = {}) => {
    const params = new URLSearchParams({ format, ...filters }).toString();
    return api.get(`/users/export?${params}`, { responseType: 'blob' });
  },
  
  // Import users from file
  importUsers: (file, options = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('options', JSON.stringify(options));
    
    return api.post('/users/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },
  
  // Get user sessions
  getUserSessions: (userId) => api.get(`/users/${userId}/sessions`),
  
  // Revoke user sessions
  revokeUserSessions: (userId, sessionId = null) => {
    if (sessionId) {
      return api.delete(`/users/${userId}/sessions/${sessionId}`);
    }
    return api.delete(`/users/${userId}/sessions`);
  },
  
  // Enable/disable MFA for user (admin)
  toggleUserMFA: (userId, enable = true) => 
    api.post(`/users/${userId}/mfa`, { enable }),
  
  // Get user API tokens
  getUserApiTokens: (userId) => api.get(`/users/${userId}/api-tokens`),
  
  // Create API token for user (admin)
  createUserApiToken: (userId, tokenData) => 
    api.post(`/users/${userId}/api-tokens`, tokenData),
  
  // Revoke user API token
  revokeUserApiToken: (userId, tokenId) => 
    api.delete(`/users/${userId}/api-tokens/${tokenId}`),
  
  // Get user audit logs
  getUserAuditLogs: (userId, filters = {}) => {
    const params = new URLSearchParams(filters).toString();
    return api.get(`/users/${userId}/audit-logs?${params}`);
  },
  
  // Get user login history
  getUserLoginHistory: (userId, limit = 50) => 
    api.get(`/users/${userId}/login-history?limit=${limit}`),
  
  // Update user permissions (admin only)
  updateUserPermissions: (userId, permissions) => 
    api.put(`/users/${userId}/permissions`, { permissions }),
  
  // Assign user to groups/teams
  assignUserToGroups: (userId, groupIds) => 
    api.post(`/users/${userId}/groups`, { groupIds }),
  
  // Remove user from groups
  removeUserFromGroups: (userId, groupIds) => 
    api.delete(`/users/${userId}/groups`, { data: { groupIds } }),
  
  // Get user groups
  getUserGroups: (userId) => api.get(`/users/${userId}/groups`),
  
  // Set user quota/limits
  setUserQuota: (userId, quotaData) => 
    api.post(`/users/${userId}/quota`, quotaData),
  
  // Get user quota
  getUserQuota: (userId) => api.get(`/users/${userId}/quota`),
  
  // Send custom notification to user
  sendUserNotification: (userId, notification) => 
    api.post(`/users/${userId}/notify`, notification),
  
  // Get user preferences
  getUserPreferences: (userId) => api.get(`/users/${userId}/preferences`),
  
  // Update user preferences
  updateUserPreferences: (userId, preferences) => 
    api.put(`/users/${userId}/preferences`, preferences),
  
  // Get user devices
  getUserDevices: (userId) => api.get(`/users/${userId}/devices`),
  
  // Revoke user device
  revokeUserDevice: (userId, deviceId) => 
    api.delete(`/users/${userId}/devices/${deviceId}`),
  
  // Check if user exists by email
  checkUserExists: (email) => api.get(`/users/check/${encodeURIComponent(email)}`),
  
  // Merge duplicate users
  mergeUsers: (primaryUserId, duplicateUserId) => 
    api.post('/users/merge', { primaryUserId, duplicateUserId }),
  
  // Restore deleted user
  restoreUser: (userId) => api.post(`/users/${userId}/restore`),
  
  // Permanently delete user
  permanentlyDeleteUser: (userId) => api.delete(`/users/${userId}/permanent`),
  
  // Get user count by status
  getUserCountByStatus: () => api.get('/users/count/status'),
  
  // Get user count by role
  getUserCountByRole: () => api.get('/users/count/role'),
  
  // Get user count by department
  getUserCountByDepartment: () => api.get('/users/count/department'),
  
  // Get user registration timeline
  getUserRegistrationTimeline: (period = '30d') => 
    api.get(`/users/timeline/registration?period=${period}`),
  
  // Get user activity timeline
  getUserActivityTimeline: (userId, period = '30d') => 
    api.get(`/users/${userId}/timeline/activity?period=${period}`),
  
  // Get user login locations
  getUserLoginLocations: (userId) => api.get(`/users/${userId}/locations`),
  
  // Set user access schedule
  setUserAccessSchedule: (userId, schedule) => 
    api.post(`/users/${userId}/schedule`, schedule),
  
  // Get user access schedule
  getUserAccessSchedule: (userId) => api.get(`/users/${userId}/schedule`),
  
  // Validate user access (check if user can access resource)
  validateUserAccess: (userId, resource, action) => 
    api.post('/users/validate-access', { userId, resource, action }),
  
  // Get user risk score
  getUserRiskScore: (userId) => api.get(`/users/${userId}/risk-score`),
  
  // Update user risk score
  updateUserRiskScore: (userId, riskScore, reason) => 
    api.post(`/users/${userId}/risk-score`, { riskScore, reason }),
  
  // Get user compliance status
  getUserComplianceStatus: (userId) => api.get(`/users/${userId}/compliance`),
  
  // Mark user compliant
  markUserCompliant: (userId, complianceData) => 
    api.post(`/users/${userId}/compliance`, complianceData),
  
  // Get user training status
  getUserTrainingStatus: (userId) => api.get(`/users/${userId}/training`),
  
  // Update user training status
  updateUserTrainingStatus: (userId, trainingData) => 
    api.post(`/users/${userId}/training`, trainingData),
  
  // Request user review (for managers)
  requestUserReview: (userId, reviewData) => 
    api.post(`/users/${userId}/review`, reviewData),
  
  // Submit user review
  submitUserReview: (userId, reviewId, reviewData) => 
    api.post(`/users/${userId}/reviews/${reviewId}`, reviewData),
  
  // Get user reviews
  getUserReviews: (userId) => api.get(`/users/${userId}/reviews`),
  
  // Archive user
  archiveUser: (userId, archiveData) => 
    api.post(`/users/${userId}/archive`, archiveData),
  
  // Unarchive user
  unarchiveUser: (userId) => api.post(`/users/${userId}/unarchive`),
  
  // Get archived users
  getArchivedUsers: () => api.get('/users/archived'),
  
  // Get user hierarchy (manager > team members)
  getUserHierarchy: (userId) => api.get(`/users/${userId}/hierarchy`),
  
  // Assign manager to user
  assignManager: (userId, managerId) => 
    api.post(`/users/${userId}/manager`, { managerId }),
  
  // Remove manager from user
  removeManager: (userId) => api.delete(`/users/${userId}/manager`),
  
  // Get user's team members (if manager)
  getUserTeam: (userId) => api.get(`/users/${userId}/team`),
  
  // Transfer user to another organization (super admin)
  transferUser: (userId, targetOrgId) => 
    api.post(`/users/${userId}/transfer`, { targetOrgId }),
  
  // Clone user permissions/roles
  cloneUserSettings: (sourceUserId, targetUserId, settings = {}) => 
    api.post('/users/clone-settings', { sourceUserId, targetUserId, settings }),
  
  // Generate user report
  generateUserReport: (userId, reportType, options = {}) => 
    api.post(`/users/${userId}/reports/${reportType}`, options),
  
  // Get user report history
  getUserReportHistory: (userId) => api.get(`/users/${userId}/reports`),
  
  // Download user report
  downloadUserReport: (userId, reportId) => 
    api.get(`/users/${userId}/reports/${reportId}/download`, { responseType: 'blob' }),
  
  // Send welcome email to user (admin)
  sendWelcomeEmail: (userId, template = 'default') => 
    api.post(`/users/${userId}/welcome-email`, { template }),
  
  // Send onboarding checklist
  sendOnboardingChecklist: (userId) => api.post(`/users/${userId}/onboarding`),
  
  // Mark onboarding complete
  markOnboardingComplete: (userId, checklist) => 
    api.post(`/users/${userId}/onboarding/complete`, { checklist }),
  
  // Get user onboarding status
  getUserOnboardingStatus: (userId) => api.get(`/users/${userId}/onboarding`),
  
  // Set user tags
  setUserTags: (userId, tags) => api.post(`/users/${userId}/tags`, { tags }),
  
  // Get user tags
  getUserTags: (userId) => api.get(`/users/${userId}/tags`),
  
  // Remove user tag
  removeUserTag: (userId, tag) => api.delete(`/users/${userId}/tags/${encodeURIComponent(tag)}`),
  
  // Search users by tags
  searchUsersByTags: (tags, operator = 'AND') => 
    api.get(`/users/tags/search?tags=${tags.join(',')}&operator=${operator}`),
  
  // Bulk update user fields
  bulkUpdateUsers: (userIds, updates) => 
    api.put('/users/bulk/update', { userIds, updates }),
  
  // Bulk delete users
  bulkDeleteUsers: (userIds, permanent = false) => 
    api.post('/users/bulk/delete', { userIds, permanent }),
  
  // Bulk export users
  bulkExportUsers: (userIds, format = 'csv') => {
    const params = new URLSearchParams({ 
      userIds: userIds.join(','), 
      format 
    }).toString();
    return api.get(`/users/bulk/export?${params}`, { responseType: 'blob' });
  },
  
  // Bulk invite users
  bulkInviteUsers: (invites, options = {}) => 
    api.post('/users/bulk/invite', { invites, options }),
  
  // Get bulk operation status
  getBulkOperationStatus: (operationId) => 
    api.get(`/users/bulk/operations/${operationId}`),
  
  // Cancel bulk operation
  cancelBulkOperation: (operationId) => 
    api.delete(`/users/bulk/operations/${operationId}`),

  bulkResetPasswords: (data) => api.post('/users/bulk/reset-passwords', data),

  // Bulk invite endpoint  
  bulkInviteUsers: (data) => api.post('/users/bulk/invite', data),
  
  // Get user audit summary
  getUserAuditSummary: (userId) => api.get(`/users/${userId}/audit-summary`),
  
  // Get user security events
  getUserSecurityEvents: (userId, limit = 50) => 
    api.get(`/users/${userId}/security-events?limit=${limit}`),
  
  // Acknowledge security event
  acknowledgeSecurityEvent: (userId, eventId) => 
    api.post(`/users/${userId}/security-events/${eventId}/acknowledge`),
  
  // Get user anomaly detection
  getUserAnomalies: (userId) => api.get(`/users/${userId}/anomalies`),
  
  // Mark anomaly as resolved
  markAnomalyResolved: (userId, anomalyId, resolution) => 
    api.post(`/users/${userId}/anomalies/${anomalyId}/resolve`, { resolution }),
  
  // Get user access patterns
  getUserAccessPatterns: (userId) => api.get(`/users/${userId}/access-patterns`),
  
  // Set user access pattern alert
  setUserAccessPatternAlert: (userId, patternId, alertConfig) => 
    api.post(`/users/${userId}/access-patterns/${patternId}/alert`, alertConfig)
};

// User Management Helper Functions
export const userManagementHelpers = {
  // Normalize role for display
  normalizeRole: (role) => {
    const roleMap = {
      'admin': 'Administrator',
      'administrator': 'Administrator',
      'manager': 'Manager',
      'developer': 'Developer',
      'auditor': 'Auditor',
      'viewer': 'Viewer',
      'user': 'User'
    };
    return roleMap[role?.toLowerCase()] || role;
  },
  
  // Get role color
  getRoleColor: (role) => {
    const colorMap = {
      'admin': 'primary',
      'administrator': 'primary',
      'manager': 'secondary',
      'developer': 'accent',
      'auditor': 'warning',
      'viewer': 'muted',
      'user': 'default'
    };
    return colorMap[role?.toLowerCase()] || 'default';
  },
  
  // Get status color
  getStatusColor: (status) => {
    const colorMap = {
      'active': 'success',
      'inactive': 'muted',
      'suspended': 'error',
      'pending': 'warning',
      'deleted': 'error'
    };
    return colorMap[status?.toLowerCase()] || 'default';
  },
  
  // Format user data for display
  formatUserForDisplay: (user) => {
    return {
      ...user,
      displayName: `${user.firstName} ${user.lastName}`.trim() || user.email,
      displayRole: userManagementHelpers.normalizeRole(user.role),
      roleColor: userManagementHelpers.getRoleColor(user.role),
      statusColor: userManagementHelpers.getStatusColor(user.status),
      initials: userManagementHelpers.getInitials(user.firstName, user.lastName, user.email)
    };
  },
  
  // Get user initials
  getInitials: (firstName, lastName, email) => {
    if (firstName && lastName) {
      return `${firstName[0]}${lastName[0]}`.toUpperCase();
    } else if (firstName) {
      return firstName.substring(0, 2).toUpperCase();
    } else if (email) {
      return email.substring(0, 2).toUpperCase();
    }
    return '??';
  },
  
  // Check if user can perform action
  canPerformAction: (currentUser, action, targetUser = null) => {
    const currentRole = currentUser?.role?.toLowerCase();
    const targetRole = targetUser?.role?.toLowerCase();
    
    // Role hierarchy
    const roleHierarchy = {
      'admin': 5,
      'administrator': 5,
      'manager': 4,
      'developer': 3,
      'auditor': 3,
      'viewer': 2,
      'user': 1
    };
    
    const currentLevel = roleHierarchy[currentRole] || 0;
    const targetLevel = targetRole ? roleHierarchy[targetRole] : 0;
    
    switch(action) {
      case 'invite_user':
        return ['admin', 'administrator', 'manager'].includes(currentRole);
      
      case 'edit_user':
        if (!targetUser) return false;
        // Can't edit yourself if admin is trying to change their own role
        if (currentUser.userId === targetUser.id && action === 'edit_user_role') {
          return currentRole === 'admin';
        }
        return currentLevel > targetLevel || currentRole === 'admin';
      
      case 'delete_user':
        if (!targetUser) return false;
        // Can't delete yourself
        if (currentUser.userId === targetUser.id) return false;
        return currentLevel > targetLevel || currentRole === 'admin';
      
      case 'reset_password':
        return ['admin', 'administrator', 'manager'].includes(currentRole);
      
      case 'view_audit_logs':
        return ['admin', 'administrator', 'manager', 'auditor'].includes(currentRole);
      
      case 'manage_keys':
        return ['admin', 'administrator', 'manager', 'developer'].includes(currentRole);
      
      case 'manage_users':
        return ['admin', 'administrator', 'manager'].includes(currentRole);
      
      default:
        return false;
    }
  },
  
  // Prepare user data for API
  prepareUserData: (userData) => {
    const data = { ...userData };
    
    // Normalize role
    if (data.role) {
      data.role = data.role.toLowerCase();
    }
    
    // Clean up empty strings
    Object.keys(data).forEach(key => {
      if (data[key] === '') {
        delete data[key];
      }
    });
    
    return data;
  },
  
  // Validate user data
  validateUserData: (userData, isNew = false) => {
    const errors = {};
    
    if (isNew) {
      if (!userData.email) {
        errors.email = 'Email is required';
      } else if (!/\S+@\S+\.\S+/.test(userData.email)) {
        errors.email = 'Invalid email format';
      }
    }
    
    if (userData.role && !['admin', 'administrator', 'manager', 'developer', 'auditor', 'viewer', 'user'].includes(userData.role.toLowerCase())) {
      errors.role = 'Invalid role';
    }
    
    if (userData.phone && !/^[\d\s\-\+\(\)]{10,15}$/.test(userData.phone.replace(/\D/g, ''))) {
      errors.phone = 'Invalid phone number';
    }
    
    return errors;
  },
  
  // Format bulk action data
  formatBulkActionData: (action, userIds, additionalData = {}) => {
    const data = {
      action,
      userIds
    };
    
    switch(action) {
      case 'changeRole':
        if (additionalData.role) {
          data.role = additionalData.role.toLowerCase();
        }
        break;
      
      case 'changeDepartment':
        if (additionalData.department) {
          data.department = additionalData.department;
        }
        break;
      
      case 'activate':
      case 'deactivate':
      case 'suspend':
      case 'delete':
        // No additional data needed
        break;
      
      default:
        Object.assign(data, additionalData);
    }
    
    return data;
  }
};

// Key Management API calls
export const PQCkeyAPI = {
  // Get all keys
  getKeys: (params = {}) => {
    const queryParams = new URLSearchParams(params).toString();
    return api.get(`/keys${queryParams ? `?${queryParams}` : ''}`);
  },
  
  // Generate ML-KEM key
  generateMLKEMKey: (keyData) => api.post('/keys/generate/ml-kem', keyData),
  
  // Generate ML-DSA key
  generateMLDSAKey: (keyData) => api.post('/keys/generate/ml-dsa', keyData),
  
  // Generate Hybrid key
  generateHybridKey: (keyData) => api.post('/keys/generate/hybrid', keyData),
  
  // Get single key details
  getKey: (keyId) => api.get(`/keys/${keyId}`),
  
  // Revoke key
  //revokeKey: (keyId, reason = '') => api.post(`/keys/${keyId}/revoke`, { reason }),
  
  // Delete key
  //deleteKey: (keyId) => api.delete(`/keys/${keyId}/delete`),
  
  // Rotate key
  rotateKey: (keyId) => api.post(`/keys/${keyId}/rotate`),
  
  // Get key statistics
  getKeyStats: () => api.get('/keys/stats'),
  
  // Get supported algorithms
  getAlgorithms: () => api.get('/keys/algorithms'),
  
  // Download public key
  downloadPublicKey: (keyId) => api.get(`/keys/${keyId}/download`, { responseType: 'blob' }),
  
  // Bulk operations
  bulkRevokeKeys: (keyIds, reason = '') => api.post('/keys/bulk/revoke', { keyIds, reason }),
  bulkDeleteKeys: (keyIds) => api.delete('/keys/bulk/delete', { data: { keyIds } }),
  
  // Cryptographic operations
  encapsulate: (keyId, data) => api.post(`/keys/${keyId}/encapsulate`, data),
  decapsulate: (keyId, ciphertext) => api.post(`/keys/${keyId}/decapsulate`, { ciphertext }),
  sign: (keyId, message) => api.post(`/keys/${keyId}/sign`, { message }),
  verify: (keyId, message, signature) => api.post(`/keys/${keyId}/verify`, { message, signature }),
  
  // Key usage history
  getKeyUsage: (keyId) => api.get(`/keys/${keyId}/usage`),

  
    revokeKey: (keyId, reason = '') => {
    return api({
      method: 'post',
      url: `/keys/${keyId}/revoke`,
      data: { reason },
      headers: {
        'Content-Type': 'application/json'
      }
    });
  },
  
  // Delete key - this works because DELETE with body needs special handling
  deleteKey: (keyId) => api.delete(`/keys/${keyId}/delete`),
  
  // Rotate key - also needs proper headers
  rotateKey: (keyId) => {
    return api({
      method: 'post',
      url: `/keys/${keyId}/rotate`,
      data: {},
      headers: {
        'Content-Type': 'application/json'
      }
    });
  },
};

export const auditAPInew = {
  // Get logs with filters
  getLogs: (params = {}) => {
    const queryParams = new URLSearchParams(params).toString();
    return api.get(`/audit/logs?${queryParams}`);
  },
  
  // Get log details
  getLogDetails: (logId) => api.get(`/audit/logs/${logId}`),
  
  // Get statistics
  getStats: (days = 30) => api.get(`/audit/stats?days=${days}`),
  
  // Export logs
  exportLogs: (format = 'json', filters = {}) => {
    const params = new URLSearchParams({ format, ...filters }).toString();
    return api.get(`/audit/export?${params}`, { responseType: 'blob' });
  },
  
  // Get user logs
  getUserLogs: (userId, params = {}) => {
    const queryParams = new URLSearchParams(params).toString();
    return api.get(`/audit/user/${userId}?${queryParams}`);
  },
  
  // Get recent events
  getRecentEvents: (limit = 20) => api.get(`/audit/recent?limit=${limit}`),
  
  // Search logs
  searchLogs: (query, params = {}) => {
    const queryParams = new URLSearchParams({ q: query, ...params }).toString();
    return api.get(`/audit/search?${queryParams}`);
  }
};


// Update the main API export
export const API = {
  auth: authAPI,
  pqcKeys: PQCkeyAPI,
  registration: registrationHelper,
  google: googleAuthHelper,
  tokens: tokenAPI,
  user: userAPI,
  organization: organizationAPI,
  audit: auditAPInew,
  auditLegacy: auditAPI,  
  keys: keyAPI,
  policies: policyAPI,
  dashboard: dashboardAPI,
  protected: protectedAPI,
  users: userManagementAPI,  // Add user management API
  userHelpers: userManagementHelpers,  // Add user helpers
  utils: authUtils,
  flow: registrationFlow,
  health: healthCheck,
  call: apiCall
};

